"use client";

import { c as _c } from "react/compiler-runtime";
import { generateJSON } from "@tiptap/core";
import type { Editor, JSONContent } from "@tiptap/react";
import { toast } from "@/lib/use-toast";
import type { EmailTemplate } from "@/types/email-template";
import { prepareEmailHtml } from "./blocks/html-fallback/prepare-html";
import { sanitizeEmailDocument } from "./blocks/html-fallback/sanitize-document";
import { getHtmlParseExtensions } from "./blocks/registry";
import { normalizeEmailContent } from "./blocks/row/normalize-content";
import { useEmailComposer } from "./context";
import { LoadTemplateMenu } from "./load-template-menu";

/**
 * Top-level rows containing a quoted thread.
 *
 * `normalizeEmailContent` wraps every top-level node in `emailRow > emailCell`,
 * so a reply's quote is nested rather than a direct child of the doc — hence the
 * descend. Returns `[]` for compose / blast, which have nothing quoted.
 */
function collectQuoteRows(editor: Editor): JSONContent[] {
  const rows: JSONContent[] = [];
  editor.state.doc.forEach(node => {
    let hasQuote = node.type.name === "emailQuote";
    if (!hasQuote) {
      node.descendants(child => {
        if (child.type.name === "emailQuote") hasQuote = true;
      });
    }
    if (hasQuote) rows.push(node.toJSON() as JSONContent);
  });
  return rows;
}

/**
 * Translate external semantic HTML into the email block editor's row/cell JSON.
 *
 * `prepareEmailHtml` is inside rather than at the call sites: it is the one door
 * foreign markup comes through — unwrapping our own scaffold, resolving a
 * stylesheet onto the elements it selects, and stripping what can execute — and
 * a second caller that skipped it would be a second, unchecked way in.
 */
export function parseHtmlToEmailContent(html: string): JSONContent {
  const parsed = generateJSON(prepareEmailHtml(html), getHtmlParseExtensions());
  return normalizeEmailContent(parsed as JSONContent) ?? {
    type: "doc",
    content: []
  };
}

/**
 * Replace the composer's body with a template, keeping any quoted thread.
 *
 * A template body is a *whole* email — clinic header and signature included —
 * so it has to swap in wholesale; layering it on top of a composer that already
 * seeded its own signature would render that signature twice. The quote is the
 * one part worth keeping, and `emailQuote` is a real node type, so it's the one
 * part we can reliably find again.
 *
 * With no quote present (compose, blast) this is simply a replace, which is why
 * every surface can share one path.
 */
export function applyTemplateBody(editor: Editor | null | undefined, template: EmailTemplate) {
  if (!editor) return;
  const quoteRows = collectQuoteRows(editor);

  // An HTML template is markup authored outside the composer. `generateJSON`
  // parses it against our own extension list, so the design maps onto real
  // blocks — the tag rules each block now declares (see blocks/html-parse.ts)
  // are what make that translation non-lossy. Going through JSON rather than
  // `setContent(html)` also lets the quote be re-attached in the same pass, the
  // way the TIPTAP_JSON branch does.
  if (template.bodyFormat === "HTML") {
    const parsed = parseHtmlToEmailContent(template.body);
    editor.commands.setContent({
      type: "doc",
      content: [...(parsed.content ?? []), ...quoteRows]
    });
    return;
  }
  let parsed: JSONContent;
  try {
    parsed = sanitizeEmailDocument(JSON.parse(template.body) as JSONContent);
  } catch {
    toast({
      title: "Failed to load template",
      variant: "error"
    });
    return;
  }
  editor.commands.setContent({
    type: "doc",
    content: [...(parsed.content ?? []), ...quoteRows]
  });
}
interface LoadTemplateProps {
  /** Called after the body is applied — e.g. to populate the subject line. */
  onSelect?: (template: EmailTemplate) => void;
  disabled?: boolean;
}

/**
 * Compound slot for the shared EmailComposer: renders the template picker and
 * applies the chosen template to the composer's editor (read from context, no
 * ref threading). Self-hides when there are no saved templates, so any surface
 * can opt in with a single line.
 */
export function LoadTemplate(t0) {
  const $ = _c(6);
  const {
    onSelect,
    disabled
  } = t0;
  const {
    editor
  } = useEmailComposer();
  let t1;
  if ($[0] !== editor || $[1] !== onSelect) {
    t1 = template => {
      applyTemplateBody(editor, template);
      onSelect?.(template);
    };
    $[0] = editor;
    $[1] = onSelect;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== disabled || $[4] !== t1) {
    t2 = <LoadTemplateMenu disabled={disabled} onSelect={t1} />;
    $[3] = disabled;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}